import { AsyncPipe, DatePipe, JsonPipe, NgFor, NgIf } from "@angular/common";
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
  signal,
  input
} from "@angular/core";
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { RouterModule } from "@angular/router";
import {
  IonicModule,
  LoadingController,
  ModalController,
  PopoverController,
  ToastController,
  isPlatform,
} from "@ionic/angular";
import { Store } from "@ngrx/store";
import { DataService } from "src/app/supabase/services/data.service";
import { Tab1Page } from "src/app/tab1/tab1.page";
import { ModeService } from "src/app/utils/mode.service";
import {
  addOrEditOpenInvoiceItem,
  clearOpenInvoiceItems,
  getOpenInvoiceItems,
  removeOpenInvoiceItem,
} from "../../invoice-ngrx/invoices.actions";
import * as invoiceSelector from "../../invoice-ngrx/invoices.selectors";
import * as customerSelector from "../../../auth/ngrx/customer.selectors";
import * as paymentSelector from "../../payment-ngrx/payments.selectors";
import { InvoiceItemDto } from "../../interfaces/invoice_item_dto";
import { InvoiceItemFormComponent } from "../invoice-item-form/invoice-item-form.component";
import { PdfPreviewComponent } from "../pdf-preview/pdf-preview.component";
import { TwoDecimal } from "src/app/utils/TwoDecimal";
import { InvoiceAddRequest } from "../../interfaces/invoice_add_request";
import { loadCustomer } from "src/app/auth/ngrx/customer.actions";
import {
  clearPaymentBalance,
  getPaymentBalance,
} from "../../payment-ngrx/payments.actions";
import { PaymentBalance } from "../../interfaces/payment_balance";
import { Subject, switchMap, takeUntil } from "rxjs";
import { LessonInvoiceAddRequest } from "../../interfaces/lesson_invoice_add_request";
import { CommentPopoverComponent } from "src/app/shared/components/comment-popover/comment-popover.component";
import { AuthService } from "src/app/supabase/services/auth.service";
import { QrBillService } from "../../services/qr-bill.service";

@Component({
    selector: "app-new-invoice-dialog",
    templateUrl: "./new-invoice-dialog.component.html",
    styleUrls: ["./new-invoice-dialog.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        IonicModule,
        FormsModule,
        RouterModule,
        ReactiveFormsModule,
        DatePipe,
    ]
})
export class NewInvoiceDialogComponent implements OnInit {
  readonly invoice = input<LessonInvoiceAddRequest | null>(null);

  invoiceForm: FormGroup;
  paymentForm: FormGroup;
  invoicePaymentForm: FormGroup;
  studentForm: FormGroup;
  isLoadingInvoiceItemsSignal = this.store.selectSignal(
    invoiceSelector.selectIsLoading
  );
  isLoadingBalanceSignal = this.store.selectSignal(
    paymentSelector.selectIsLoading
  );
  today = new Date();
  formattedDate = this.today.toISOString().substr(0, 10);
  includePayment: boolean = false;
  subscriptionLessons: number = 0;
  customerSignal = this.store.selectSignal(customerSelector.selectCustomer);
  isGeneratingInvoiceSignal = signal(false);

  constructor(
    public modeService: ModeService,
    private store: Store<any>,
    private fb: FormBuilder,
    private authService: AuthService,
    public modalController: ModalController,
    private cdr: ChangeDetectorRef,
    private qrBillService: QrBillService,
    private loadingCtrl: LoadingController,
    private toastController: ToastController,
    private popoverController: PopoverController
  ) {
    this.paymentForm = this.fb.group({
      payment: [
        null,
        [Validators.min(0), Validators.max(this.invoiceForm?.value?.amount)],
      ],
      payment_method: ["Bar"],
      date: [this.formattedDate],
    });

    this.invoiceForm = this.fb.group({
      student_id: ["", Validators.required],
      date: [this.formattedDate, Validators.required],
      amount: [""],
      total_amount: ["", Validators.required],
      type: [this.customerSignal().invoice_due_days ?? 7, Validators.required],
      invoice_items: [new Array<InvoiceItemDto>(), Validators.required],
      notes: [""],
      user_id: [""],
    });

    this.invoicePaymentForm = this.fb.group({
      student_balance: [null],
      open_payments: [new Array<PaymentBalance>()],
    });
  }

  private destroy$ = new Subject<void>();

  ngOnInit() {
    this.store.dispatch(clearOpenInvoiceItems());
    this.store.dispatch(clearPaymentBalance());
    this.store.dispatch(loadCustomer());

    this.store
      .select(invoiceSelector.selectNewInvoiceItems)
      .pipe(
        switchMap((invoice_items) => {
          this.subscriptionLessons =
            this.invoiceForm.get("student_id").value.subscription_lessons;

          invoice_items = invoice_items.map((invoice_item) => {
            let updatedInvoiceItem = { ...invoice_item };
            if (
              (updatedInvoiceItem.unit === "Lektion" ||
                updatedInvoiceItem.unit === "Lektionen") &&
              !updatedInvoiceItem.subscription_id &&
              updatedInvoiceItem.subscription_payment
            ) {
              if (updatedInvoiceItem.amount <= this.subscriptionLessons && updatedInvoiceItem.prize !== 0) {
                updatedInvoiceItem.description = 'Reduzierung um ' + (updatedInvoiceItem.amount > 1 ? updatedInvoiceItem.amount + " Lektionen" + " (Abo-Guthaben)" : updatedInvoiceItem.amount + " Lektion" + " (Abo-Guthaben)");
                updatedInvoiceItem.prize = 0;
                this.subscriptionLessons =
                  this.subscriptionLessons - updatedInvoiceItem.amount;
              } else if (
                this.subscriptionLessons !== 0 &&
                this.subscriptionLessons !== null &&
                updatedInvoiceItem.prize !== 0
              ) {
                updatedInvoiceItem.description = 'Reduzierung um ' + (this.subscriptionLessons > 1 ? this.subscriptionLessons + " Lektionen" + " (Abo-Guthaben)" : this.subscriptionLessons + " Lektion" + " (Abo-Guthaben)");
                updatedInvoiceItem.prize =
                  updatedInvoiceItem.prize -
                  (updatedInvoiceItem.prize / updatedInvoiceItem.amount) *
                    this.subscriptionLessons;
                this.subscriptionLessons = 0;
              }
            }
            else if (updatedInvoiceItem.subscription_id) {
              this.subscriptionLessons =
                this.subscriptionLessons + updatedInvoiceItem.amount;
            }

            return updatedInvoiceItem;
          });

          this.invoiceForm.controls["invoice_items"].setValue(invoice_items);
          this.invoiceForm.controls["total_amount"].setValue(
            invoice_items.reduce((acc, curr) => acc + curr.prize, 0)
          );
          this.invoiceForm.controls["amount"].setValue(
            invoice_items.reduce((acc, curr) => acc + curr.prize, 0)
          );

          this.cdr.detectChanges();
          return this.store.select(paymentSelector.selectOpenPayments);
        }),
        takeUntil(this.destroy$) // Use takeUntil to unsubscribe when the component is destroyed
      )
      .subscribe((openPayments) => {
        if (!openPayments || openPayments.length === 0) {
          return;
        }
        openPayments = [...openPayments];
        openPayments = openPayments?.sort(
          (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
        );

        let total_amount = this.invoiceForm.value.total_amount;
        this.invoicePaymentForm.controls["open_payments"].setValue([]);

        openPayments.forEach((openPayment) => {
          openPayment = { ...openPayment };

          if (openPayment.open_amount < total_amount) {
            if (openPayment.open_amount === 0) return;
            this.invoicePaymentForm.controls["open_payments"].setValue([
              ...this.invoicePaymentForm.value.open_payments,
              openPayment,
            ]);
            total_amount -= openPayment.open_amount;
          } else {
            openPayment.open_amount = total_amount;
            total_amount -= openPayment.open_amount;
            if (openPayment.open_amount === 0) return;
            this.invoicePaymentForm.controls["open_payments"].setValue([
              ...this.invoicePaymentForm.value.open_payments,
              openPayment,
            ]);
            return;
          }
        });

        this.invoiceForm.controls["amount"].setValue(
          this.invoiceForm.value.invoice_items.reduce(
            (acc, curr) => acc + curr.prize,
            0
          ) -
            this.paymentForm.value.payment -
            (this.invoicePaymentForm.value.open_payments?.reduce(
              (acc, curr) => acc + curr.open_amount,
              0
            ) ?? 0)
        );
        this.paymentForm.controls["payment"].setValidators([
          Validators.min(0),
          Validators.max(this.invoiceForm?.value?.amount),
        ]);
        this.cdr.detectChanges();
      });
    this.store
      .select(paymentSelector.selectOpenPayments)
      .subscribe((openPayments) => {
        // the studentBalance is the sum of the open_amount of all openPayments
        let studentBalance;
        if (openPayments.length > 0) {
          studentBalance = openPayments.reduce(
            (acc, curr) => acc + curr.open_amount,
            0
          );
        }

        this.invoicePaymentForm.controls["student_balance"].setValue(
          studentBalance
        );
        this.cdr.detectChanges();
      });

    const invoice = this.invoice();
    if (invoice) {
      this.invoiceForm.controls["student_id"].setValue(invoice.student_id);
      this.invoicePaymentForm.controls["open_payments"].setValue([]);
      this.invoicePaymentForm.controls["student_balance"].setValue(null);
      this.store.dispatch(
        getPaymentBalance({ student_id: invoice.student_id.student_id })
      );
      if (
        invoice.invoice_items?.length > 0 &&
        invoice.invoice_items !== null &&
        invoice.invoice_items[0] !== null
      ) {
        for (let i = 0; i < invoice.invoice_items.length; i++) {
          this.store.dispatch(
            addOrEditOpenInvoiceItem({
              invoiceItem: invoice.invoice_items[i],
            })
          );
        }
      }
      this.cdr.detectChanges();
    }
  }

  isItemInInvoice(item) {
    return this.invoice()?.invoice_items?.some((i) => i?.id === item?.id);
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ionViewDidLeave() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getOpenInvoiceItems() {
    this.store.dispatch(
      getOpenInvoiceItems({
        student_id: this.invoiceForm.controls.student_id.value.student_id,
      })
    );
  }

  cancel() {
    this.modalController.dismiss();
  }

  addPayment() {
    this.includePayment = !this.includePayment;
    const amount = this.invoiceForm.value.amount;
    this.paymentForm.controls["payment"].setValue(amount);
    this.updatePayment();
    this.cdr.detectChanges();
  }

  async previewInvoice() {
    this.isGeneratingInvoiceSignal.set(true);

    var pdfSrc = "";
    var errorMessage = "Die Rechnung konnte nicht erstellt werden";
    try {
      // Make a copy of the original invoice form value
      const originalInvoiceFormValue = { ...this.invoiceForm.value };
      const originalPaymentFormValue = { ...this.paymentForm.value };
      const originalInvoicePaymentFormValue = {
        ...this.invoicePaymentForm.value,
      };

      // format the dates to be a string in the format dd.mm.yyyy
      var invoicePdfDto = this.invoiceForm.value;
      invoicePdfDto.customer = this.customerSignal();
      invoicePdfDto.payments = [this.paymentForm.value];
      invoicePdfDto.invoice_payments =
        this.invoicePaymentForm.value.open_payments;

      // invoice for pdf
      let invoicePdf = originalInvoiceFormValue;

      invoicePdf.customer = this.customerSignal();

      invoicePdf.payments = [originalPaymentFormValue];
      invoicePdf.payments[0].date = new Date(
        invoicePdf.payments[0].date
      ).toLocaleDateString("fr-CH");

      // the paid amount is the sum of all open_amount of the invoice_payments
      if (originalInvoicePaymentFormValue.open_payments.length > 0) {
        let paid_amount = originalInvoicePaymentFormValue.open_payments.reduce(
          (acc, curr) => acc + curr.open_amount,
          0
        );
        invoicePdfDto.paid_amount = paid_amount;

        invoicePdf.invoice_payments =
          originalInvoicePaymentFormValue.open_payments;
        invoicePdf.invoice_payments = invoicePdf.invoice_payments.map(
          (payment) => {
            return {
              date: new Date(payment.date).toLocaleDateString("fr-CH"),
              payment: payment.open_amount,
            };
          }
        );
        invoicePdf.payments = [
          ...invoicePdf.payments,
          ...invoicePdf.invoice_payments,
        ];
      }

      const invoiceDate = invoicePdfDto.date;
      invoicePdf.date = new Date(invoicePdf.date).toLocaleDateString("fr-CH");

      try {
        const blobUrl = await this.qrBillService.generateQrInvoice(invoicePdf);
        pdfSrc = blobUrl;
      } catch (error) {
        if (error.message.includes("IBAN")) {
          errorMessage =
            "Die angegebene IBAN ist ungültig. Bitte überprüfe die IBAN in deinen Einstellungen und versuche es erneut.";
        }
        throw error;
      }

      const invoiceAddRequest: InvoiceAddRequest = {
        customer_id: invoicePdfDto.customer.customer_id,
        // for the due_date we need to add invoicePdfDto.type (type number) to the date (type DateTime)
        due_date: new Date(
          new Date(invoiceDate).getTime() +
            invoicePdfDto.type * 24 * 60 * 60 * 1000
        ).toISOString(),
        invoice_date: invoicePdfDto.date,
        mail_id: null,
        student: invoicePdfDto.student_id,
        total_amount: invoicePdfDto.total_amount,
        paid_amount: invoicePdfDto.paid_amount,
        amount: invoicePdfDto.amount,
        subscription_lessons: this.invoiceForm.value.student_id.subscription_lessons - this.subscriptionLessons,
        user_id: this.authService.getCurrentUserId(),
        invoice_item: invoicePdfDto.invoice_items.map((invoiceItem) => {
          return {
            appointment_id: invoiceItem.appointment_id || null,
            subscription_id: invoiceItem.subscription_id || null,
            name: invoiceItem.name,
            prize: invoiceItem.prize,
            unit: invoiceItem.unit,
            amount: invoiceItem.amount,
            date: invoiceItem.date,
          };
        }),
        payment: invoicePdfDto.payments.map((payment) => {
          return {
            amount: payment.payment,
            date: payment.date,
            payment_method: payment.payment_method,
          };
        }),
        invoice_payment: invoicePdfDto.invoice_payments.map((openPayment) => {
          return {
            amount: openPayment.open_amount,
            payment_id: openPayment.payment_id,
            date: openPayment.date,
          };
        }),
        student_subscription_lessons: this.subscriptionLessons,
      };

      this.isGeneratingInvoiceSignal.set(false);

      const modal = this.modalController.create({
        component: PdfPreviewComponent,
        componentProps: {
          pdfSrc: signal(pdfSrc),
          invoiceAddRequest: signal(invoiceAddRequest),
        },
        cssClass: "invoice-preview-modal",
      });
      modal.then((modal) => modal.present());

      (await modal).onDidDismiss().then((result) => {
        if (result.data) {
          this.cancel();
        }
      });
    } catch (error) {
      this.isGeneratingInvoiceSignal.set(false);

      // show an error
      const toast = await this.toastController.create({
        message: errorMessage,
        duration: 3000,
        position: "bottom",
      });

      await toast.present();
    }
  }

  isInTheFuture(item: InvoiceItemDto) {
    return new Date(item.date) > new Date();
  }

  async selectPosition(item?: InvoiceItemDto) {
    const modal = await this.modalController.create({
      component: InvoiceItemFormComponent,
      componentProps: {
        invoiceItem: signal(item),
      },
    });
    modal.onDidDismiss().then((result) => {
      if (result.data) {
        // convert prize to number
        result.data.prize = +result.data.prize;
        if (result.data.id === null || result.data.id === undefined) {
          result.data.id = Math.floor(Math.random() * 1000000000);
        }
        this.store.dispatch(
          addOrEditOpenInvoiceItem({ invoiceItem: result.data })
        );
        this.updatePayment();
        this.cdr.detectChanges();
      }
    });

    return modal.present();
  }

  updatePayment() {
    this.invoiceForm.controls["amount"].setValue(
      this.invoiceForm.controls["invoice_items"].value.reduce(
        (acc, curr) => acc + curr.prize,
        0
      ) -
        this.paymentForm.value.payment -
        this.invoicePaymentForm.value.open_payments.reduce(
          (acc, curr) => acc + curr.open_amount,
          0
        )
    );
  }

  async selectStudent() {
    const modal = await this.modalController.create(
      isPlatform("desktop") ||
        isPlatform("phablet") ||
        isPlatform("ipad") ||
        (isPlatform("tablet") && !isPlatform("mobile") && !isPlatform("iphone"))
        ? {
            component: Tab1Page,
            componentProps: {
              isSelection: signal(true),
            },
            canDismiss: true,
            showBackdrop: true,
          }
        : {
            component: Tab1Page,
            componentProps: {
              isSelection: signal(true),
            },
            canDismiss: true,
            showBackdrop: true,
            backdropDismiss: true,
            breakpoints: [0, 0.6, 1],
            initialBreakpoint: 0.6,
          }
    );
    modal.onDidDismiss().then((result) => {
      if (result.data) {
        this.invoiceForm.controls["student_id"].setValue(result.data);
        this.invoicePaymentForm.controls["open_payments"].setValue([]);
        this.invoicePaymentForm.controls["student_balance"].setValue(null);
        this.store.dispatch(
          getPaymentBalance({ student_id: result.data.student_id })
        );
        this.store.dispatch(clearOpenInvoiceItems());
        this.cdr.detectChanges();
      }
    });

    return modal.present();
  }

  checkItem(event: any) {
    // stop the event from bubbling up
    event.stopPropagation();
  }

  twoDecimal(inputNumber: number) {
    return TwoDecimal(inputNumber);
  }

  removeItem(invoiceItem) {
    this.store.dispatch(removeOpenInvoiceItem({ invoiceItem }));
  }

  async showExplanation(event: any, invoiceItem: InvoiceItemDto) {
    event.stopPropagation();
    let comment;
    if (invoiceItem.subscription_id) {
      comment =
        "Die Anzahl Lektionen werden als Guthaben auf dem Konto des Schülers gespeichert.";
    } else if (
      invoiceItem.prize === 0 &&
      invoiceItem.prize !== invoiceItem.initial_prize
    ) {
      comment =
        "Diese Fahrstunde wird vom Lektionen Guthaben des Schülers abgezogen und muss daher nicht mehr bezahlt werden.";
    } else if (invoiceItem.prize !== invoiceItem.initial_prize) {
      comment =
        "Das Lektionen Guthaben reicht nicht aus, um die ganze Fahrstunde auszugleichen. Daher muss die Differenz noch bezahlt werden.";
    } else {
      comment =
        "Diese Dienstleistung hat noch nicht stattgefunden, kann jedoch bereits jetzt durch Vorauszahlung abgerechnet werden.";
    }

    const popover = await this.popoverController.create({
      component: CommentPopoverComponent,
      componentProps: {
        comment: signal(comment),
      },
    });

    await popover.present();
  }
}
